import { Check, X, Minus } from "lucide-react";
import type { ReactNode } from "react";

type Cell = true | false | "partial" | string;

const rows: { label: string; reputely: Cell; vendors: Cell; agency: Cell; diy: Cell }[] = [
  {
    label: "Commercial model",
    reputely: "Monthly platform",
    vendors: "Per successful removal",
    agency: "Retainer or project",
    diy: "Internal time",
  },
  {
    label: "Primary job",
    reputely: "Continuous catalog protection",
    vendors: "Review takedowns",
    agency: "Manual operations",
    diy: "Manual triage",
  },
  {
    label: "Continuous monitoring",
    reputely: true,
    vendors: "partial",
    agency: "partial",
    diy: false,
  },
  {
    label: "Policy violation detection",
    reputely: true,
    vendors: "partial",
    agency: "partial",
    diy: false,
  },
  {
    label: "Appeal generation",
    reputely: "Unlimited by plan",
    vendors: "Per case",
    agency: "Manual",
    diy: "Manual",
  },
  {
    label: "Team alerts",
    reputely: true,
    vendors: "partial",
    agency: "partial",
    diy: false,
  },
  {
    label: "Catalog-wide insights",
    reputely: true,
    vendors: "Takedown-focused",
    agency: "Custom",
    diy: false,
  },
];

function Mark({ v }: { v: Cell }): ReactNode {
  if (v === true) return <Check className="h-4 w-4 text-emerald-600 inline" />;
  if (v === false) return <X className="h-4 w-4 text-slate-300 dark:text-slate-600 inline" />;
  if (v === "partial") return <Minus className="h-4 w-4 text-amber-500 inline" />;
  return <span className="text-sm text-slate-800 dark:text-slate-200">{v}</span>;
}

export function ComparisonTable() {
  return (
    <section className="max-w-6xl mx-auto px-6 py-16">
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
          How we compare
        </h2>
        <p className="mt-3 text-slate-600 dark:text-slate-400">
          A practical look at Reputely&rsquo;s model versus common alternatives.
        </p>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-900">
            <tr>
              <th className="text-left font-medium text-slate-500 dark:text-slate-400 px-5 py-3"></th>
              <th className="text-left font-medium text-slate-900 dark:text-slate-100 px-5 py-3">
                Reputely
              </th>
              <th className="text-left font-medium text-slate-500 dark:text-slate-400 px-5 py-3">
                Pay-per-removal vendors
              </th>
              <th className="text-left font-medium text-slate-500 dark:text-slate-400 px-5 py-3">
                Reputation agencies
              </th>
              <th className="text-left font-medium text-slate-500 dark:text-slate-400 px-5 py-3">
                Doing it yourself
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={r.label}
                className={i % 2 === 0 ? "bg-white dark:bg-slate-950" : "bg-slate-50/50 dark:bg-slate-900/50"}
              >
                <td className="px-5 py-3 text-slate-700 dark:text-slate-300">{r.label}</td>
                <td className="px-5 py-3"><Mark v={r.reputely} /></td>
                <td className="px-5 py-3"><Mark v={r.vendors} /></td>
                <td className="px-5 py-3"><Mark v={r.agency} /></td>
                <td className="px-5 py-3"><Mark v={r.diy} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
